import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import LoadingScreen from '../shared/ui/LoadingScreen';
import ErrorScreen from '../shared/ui/ErrorScreen';
import { GAME_BY_ID } from '../shared/games';
import {
  formatDateLabel,
  isQuestFailed,
  loadTodaysQuest,
  markQuestCompleted,
  markQuestFailed,
  questResult,
  QUEST_FAST_BONUS_COINS,
  QUEST_FAST_THRESHOLD_SECONDS,
  type DailyQuest,
} from '../shared/daily';
import type { GauntletControls } from '../shared/gauntletStep';
import GauntletStep from './GauntletSteps';
import './gauntlet.css';

const BASE = import.meta.env.BASE_URL;

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export default function GauntletPage() {
  const [quest, setQuest] = useState<DailyQuest | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    loadTodaysQuest()
      .then((q) => {
        if (cancelled) return;
        if (!q) {
          setLoadError('No Quest Gauntlet published for today yet.');
          return;
        }
        setQuest(q);
      })
      .catch((e) => !cancelled && setLoadError(String(e?.message ?? e)));
    return () => {
      cancelled = true;
    };
  }, []);

  if (loadError) return <ErrorScreen message={loadError} />;
  if (!quest) {
    return (
      <div className="gt-page">
        <LoadingScreen label="Assembling today's gauntlet…" />
      </div>
    );
  }

  return <GauntletRun key={quest.dateKey} quest={quest} />;
}

type Phase = 'intro' | 'running' | 'complete' | 'failed';

function GauntletRun({ quest }: { quest: DailyQuest }) {
  const priorResult = questResult(quest.dateKey);
  const priorFailed = isQuestFailed(quest.dateKey);

  const [phase, setPhase] = useState<Phase>(
    priorResult ? 'complete' : priorFailed ? 'failed' : 'intro',
  );
  const [stepIndex, setStepIndex] = useState(0);
  const [livesRemaining, setLivesRemaining] = useState(quest.lives);
  const [hintsRemaining, setHintsRemaining] = useState(quest.totalHints);
  const [hintSignal, setHintSignal] = useState(0);
  /** Seconds added by wrong answers — folded into the displayed timer. */
  const [penaltySeconds, setPenaltySeconds] = useState(0);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [finalElapsed, setFinalElapsed] = useState<number | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const hintsUsed = quest.totalHints - hintsRemaining;
  const livesLost = quest.lives - livesRemaining;

  // One ticking clock for the whole run, exactly as the iOS host does.
  useEffect(() => {
    if (phase !== 'running') return;
    const id = window.setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(id);
  }, [phase]);

  const elapsed = useMemo(() => {
    if (finalElapsed != null) return finalElapsed;
    if (startedAt == null) return 0;
    return Math.max(0, (now - startedAt) / 1000) + penaltySeconds;
  }, [finalElapsed, startedAt, now, penaltySeconds]);

  const flashTimer = useRef<number | undefined>(undefined);
  const showFlash = useCallback((msg: string) => {
    setFlash(msg);
    clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 1800);
  }, []);
  useEffect(() => () => clearTimeout(flashTimer.current), []);

  const start = useCallback(() => {
    setStartedAt(Date.now());
    setNow(Date.now());
    setPhase('running');
  }, []);

  const finish = useCallback(
    (totalElapsed: number) => {
      setFinalElapsed(totalElapsed);
      const fast = totalElapsed <= QUEST_FAST_THRESHOLD_SECONDS;
      const coins = quest.coinsReward + (fast ? QUEST_FAST_BONUS_COINS : 0);
      markQuestCompleted(quest.dateKey, {
        totalElapsed,
        hintsUsed,
        livesLost,
        coinsEarned: coins,
      });
      setPhase('complete');
    },
    [quest, hintsUsed, livesLost],
  );

  // State updaters must stay pure — React double-invokes them in StrictMode, so
  // deciding "was that the last step?" inside one would finish the run twice.
  // Both handlers compute from the current value and update outside instead.
  const onSolved = useCallback(() => {
    const next = stepIndex + 1;
    if (next >= quest.steps.length) {
      // Snapshot the timer at the moment the final step resolves.
      const total =
        startedAt == null
          ? 0
          : Math.max(0, (Date.now() - startedAt) / 1000) + penaltySeconds;
      finish(total);
      return;
    }
    setStepIndex(next);
    showFlash(`Step ${stepIndex + 1} cleared`);
  }, [stepIndex, quest.steps.length, startedAt, penaltySeconds, finish, showFlash]);

  /** A wrong answer: one life, plus the configured time penalty. */
  const onWrong = useCallback(() => {
    setPenaltySeconds((p) => p + quest.timePenaltySeconds);
    const next = livesRemaining - 1;
    setLivesRemaining(Math.max(0, next));
    if (next <= 0) {
      markQuestFailed(quest.dateKey);
      setPhase('failed');
      return;
    }
    showFlash(`−1 life · +${quest.timePenaltySeconds}s`);
  }, [quest, livesRemaining, showFlash]);

  const useHint = useCallback(() => {
    if (hintsRemaining <= 0) return;
    setHintsRemaining((h) => h - 1);
    setHintSignal((s) => s + 1);
  }, [hintsRemaining]);

  const controls: GauntletControls = useMemo(
    () => ({ onSolved, onWrong, hintSignal }),
    [onSolved, onWrong, hintSignal],
  );

  const step = quest.steps[stepIndex];
  const stepGame = step ? GAME_BY_ID[step.gameId] : undefined;

  // ---- Intro

  if (phase === 'intro') {
    return (
      <div className="gt-page">
        <div className="gt-intro">
          <Link to="/" className="gt-back">
            ← Games
          </Link>
          <div className="gt-intro-card">
            <div className="gt-intro-kicker">DAILY QUEST</div>
            <h1 className="gt-intro-title">Quest Gauntlet</h1>
            <p className="gt-intro-sub">
              {quest.steps.length} puzzles. One shot. One timer.
            </p>

            <div className="gt-intro-seq">
              {quest.steps.map((s, i) => (
                <span key={i} className="gt-intro-step">
                  <span className="gt-intro-step-num">{i + 1}</span>
                  <img
                    className="gt-intro-step-art"
                    src={`${BASE}games/${s.gameId}.png`}
                    alt=""
                    loading="lazy"
                  />
                  <span className="gt-intro-step-name">
                    {GAME_BY_ID[s.gameId]?.title ?? s.gameId}
                  </span>
                </span>
              ))}
            </div>

            <div className="gt-intro-rules">
              <span className="chip">❤️ {quest.lives} lives</span>
              <span className="chip">💡 {quest.totalHints} hints</span>
              <span className="chip">⏱ +{quest.timePenaltySeconds}s per miss</span>
              <span className="chip">🪙 {quest.coinsReward}</span>
            </div>

            <button type="button" className="btn btn-primary gt-start" onClick={start}>
              Start the gauntlet
            </button>
            <div className="gt-intro-date">
              {formatDateLabel(quest.dateKey, quest.isToday)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---- Complete / failed

  if (phase === 'complete' || phase === 'failed') {
    const result = questResult(quest.dateKey);
    const won = phase === 'complete';
    return (
      <div className="gt-page">
        <div className="gt-intro">
          <Link to="/" className="gt-back">
            ← Games
          </Link>
          <div className={'gt-intro-card' + (won ? ' is-won' : ' is-failed')}>
            <div className="gt-result-emoji">{won ? '🏆' : '💀'}</div>
            <h1 className="gt-intro-title">{won ? 'Gauntlet cleared' : 'Gauntlet failed'}</h1>
            <p className="gt-intro-sub">
              {won
                ? `Finished in ${formatTime(result?.totalElapsed ?? elapsed)}.`
                : 'Out of lives — try again tomorrow.'}
            </p>

            {won && result && (
              <div className="gt-result-stats">
                <div className="gt-result-stat">
                  <span className="gt-result-value">{formatTime(result.totalElapsed)}</span>
                  <span className="gt-result-label">TIME</span>
                </div>
                <div className="gt-result-stat">
                  <span className="gt-result-value">{result.hintsUsed}</span>
                  <span className="gt-result-label">HINTS</span>
                </div>
                <div className="gt-result-stat">
                  <span className="gt-result-value">{result.livesLost}</span>
                  <span className="gt-result-label">LIVES LOST</span>
                </div>
                <div className="gt-result-stat">
                  <span className="gt-result-value">{result.coinsEarned}</span>
                  <span className="gt-result-label">COINS</span>
                </div>
              </div>
            )}

            {won && result && result.totalElapsed <= QUEST_FAST_THRESHOLD_SECONDS && (
              <div className="gt-bonus">⚡ Fast completion bonus — +{QUEST_FAST_BONUS_COINS}</div>
            )}

            <Link to="/" className="btn btn-secondary gt-start">
              Back to games
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // ---- Running

  return (
    <div className="gt-page">
      <header className="gt-hud">
        <div className="container gt-hud-row">
          <Link to="/" className="gt-back" aria-label="Leave the gauntlet">
            ←
          </Link>

          <div className="gt-hud-steps">
            {quest.steps.map((_, i) => (
              <span
                key={i}
                className={
                  'gt-hud-step' +
                  (i < stepIndex ? ' is-done' : '') +
                  (i === stepIndex ? ' is-current' : '')
                }
              >
                {i + 1}
              </span>
            ))}
          </div>

          <div className="gt-hud-timer">{formatTime(elapsed)}</div>
        </div>

        <div className="container gt-hud-row gt-hud-row-2">
          <div className="gt-lives" aria-label={`${livesRemaining} lives remaining`}>
            {Array.from({ length: quest.lives }).map((_, i) => (
              <span
                key={i}
                className={'gt-life' + (i >= livesRemaining ? ' is-lost' : '')}
                aria-hidden
              >
                ♥
              </span>
            ))}
          </div>

          <div className="gt-step-name">{stepGame?.title ?? step?.gameId}</div>

          <button
            type="button"
            className="btn btn-accent gt-hint"
            onClick={useHint}
            disabled={hintsRemaining <= 0}
          >
            💡 {hintsRemaining}
          </button>
        </div>
      </header>

      {flash && <div className="gt-flash">{flash}</div>}

      <main className="container gt-body">
        {step ? (
          <GauntletStep
            key={stepIndex}
            gameId={step.gameId}
            puzzle={step.puzzle}
            dateKey={quest.dateKey}
            controls={controls}
          />
        ) : null}

        {step && !stepGame && (
          <div className="gt-unsupported">
            This step ({step.gameId}) isn't playable on the web yet.
            <button type="button" className="btn btn-secondary" onClick={onSolved}>
              Skip step
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
